import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select } from '@mantine/core';

import { getHourMeterSummary } from '../../../../../slices/thunks';
import OChartCard from '../../../../../Components/OElements/OChartCard';
import OBarChart from '../../../../../Components/OElements/OBarChart';

const HOUR_SUMMARY_KEY = 'hour_summary';

const HourMeterPowerGenerationSummaryEChart = ({
	establishmentId,
	title,
	defaultFilters = {
		option: 'semana',
		group: 'centro',
	},
	minHeight,
	maxHeight,
}) => {
	const dispatch = useDispatch();
	const [selectedDate, setSelectedDate] = useState(defaultFilters.option);
	const [selectedGroup, setSelectedGroup] = useState(defaultFilters.group);

	const infoHourSummary = useSelector(
		state => state.FuelSupply.hourmeterSummary?.[HOUR_SUMMARY_KEY] || null
	);

	const fetchHourSummary = useCallback(
		(idEstablishment, filter, type, group) => {
			dispatch(
				getHourMeterSummary({
					establishment_id: idEstablishment,
					filter: filter,
					type: type,
					group: group,
				})
			);
		},
		[dispatch]
	);

	useEffect(() => {
		if (!establishmentId) return;
		fetchHourSummary(
			establishmentId,
			selectedDate,
			HOUR_SUMMARY_KEY,
			selectedGroup
		);
	}, [
		establishmentId,
		selectedDate,
		HOUR_SUMMARY_KEY,
		selectedGroup,
		fetchHourSummary,
	]);

	const { series, categories, legendData } = useMemo(() => {
		if (!infoHourSummary?.data)
			return { series: [], categories: [], legendData: [] };

		const categoriesLocal = infoHourSummary.data.map(item => item.date);
		const dataValues = infoHourSummary.data.map(
			item => Number(item.total_hours) || 0
		);

		return {
			series: [
				{
					name: 'Total Horas',
					type: 'bar',
					data: dataValues,
				},
			],
			categories: categoriesLocal,
			legendData: ['Total Horas'],
		};
	}, [infoHourSummary?.data]);

	const options = useMemo(
		() => ({
			tooltip: {
				trigger: 'axis',
				axisPointer: { type: 'shadow' },
				formatter: function (params) {
					if (!params || !params.length) return '';
					const axisValue = params[0].axisValue;
					const point = infoHourSummary?.data?.find(
						x => x.date === axisValue
					);
					const hours = params[0].value;
					const recs = point?.records_count || 0;
					return (
						axisValue +
						'<br/>' +
						(params[0].marker || '') +
						' Total Horas: ' +
						hours +
						' h'
					);
				},
			},
			legend: {
				data: legendData,
				orient: 'horizontal',
				bottom: 10,
				type: 'scroll',
			},
			xAxis: [{ type: 'category', data: categories }],
			yAxis: [{ type: 'value', name: 'Horas' }],
			series: series,
		}),
		[series, categories, legendData, infoHourSummary?.data]
	);

	const noData = !infoHourSummary?.data?.length;
	const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
	useEffect(() => {
		if (Array.isArray(infoHourSummary?.data)) {
			setHasLoadedOnce(true);
		}
	}, [infoHourSummary?.data]);

	const actions = (
		<div className='btn-box-dashboard' style={{ display: 'flex', gap: 8 }}>
			<Select
				placeholder='Agrupación'
				data={[
					{ label: 'Por Centro', value: 'centro' },
					{ label: 'Por Fuente', value: 'fuente' },
				]}
				value={selectedGroup}
				onChange={value => value && setSelectedGroup(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
			<Select
				placeholder='Fecha'
				data={[
					{ label: 'Ciclo', value: 'ciclo' },
					{ label: 'Año', value: 'año' },
					{ label: 'Semestre', value: 'semestre' },
					{ label: 'Semana', value: 'semana' },
				]}
				value={selectedDate}
				onChange={value => value && setSelectedDate(value)}
				size='xs'
				radius='lg'
				styles={{ input: { width: 100 } }}
			/>
		</div>
	);

	return (
		<OChartCard
			title={title}
			actions={actions}
			isLoading={infoHourSummary?.isLoading && !hasLoadedOnce}
			error={infoHourSummary?.error}
			isEmpty={noData}
			emptyIconClass='ri-time-line'
			emptyMessage='No cuenta con registro de horas de generación'
		>
			<OBarChart
				options={options}
				minHeight={minHeight}
				maxHeight={maxHeight}
			/>
		</OChartCard>
	);
};

export default HourMeterPowerGenerationSummaryEChart;
